import React, { TouchEvent } from "react"

import { graphql } from "gatsby"
import { FurtherInformationsFieldsFragment } from "../../../../../../graphql-types"

import * as styles from "./further-informations.module.scss"
import useWindowWidth from "../../../../../hooks/use-window-width"
import ArticleCard from "../../../../../components/home/articleCard/articleCard"
import { truncateText } from "../../../../../tools/functions"
import BackArrowSvg from "../../../../../../static/vectors/backArrowV2.svg"
import ArrowSvg from "../../../../../../static/vectors/arrowV2.svg"
import { ReactSVG } from "react-svg"

type FurtherInformationsProps = {
  data: FurtherInformationsFieldsFragment
  locale: string
  projectsNewsInsightsPageSlug: string
}

const FurtherInformations: React.FC<FurtherInformationsProps> = ({
  data,
  locale,
  projectsNewsInsightsPageSlug,
}) => {
  const defaultLocale = "fr"
  const isMobile = useWindowWidth() <= 825
  const isTablet = useWindowWidth() <= 1300
  const [currentIndex, setCurrentIndex] = React.useState(0)
  const [touchStart, setTouchStart] = React.useState<number | null>(null)

  const handlePrevClick = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1))
  }

  const handleNextClick = () => {
    setCurrentIndex((prev) => Math.min(data.articles.length - (isMobile ? 1 : isTablet ? 2 : 3), prev + 1))
  }

  const handleTouchStart = (e: TouchEvent) => {
    setTouchStart(e.touches[0].clientX)
  }

  const handleTouchEnd = (e: TouchEvent) => {
    if (touchStart === null) return

    const touchEnd = e.changedTouches[0].clientX
    const diff = touchStart - touchEnd

    if (Math.abs(diff) > 50) { // minimum swipe distance
      if (diff > 0) {
        // Swipe left
        handleNextClick()
      } else {
        // Swipe right
        handlePrevClick()
      }
    }
    setTouchStart(null)
  }

  const visibleArticles = isMobile 
    ? data.articles.slice(currentIndex, currentIndex + 1)
    : isTablet
      ? data.articles.slice(currentIndex, currentIndex + 2)
      : data.articles.slice(currentIndex, currentIndex + 3)
    
  const canScrollLeft = currentIndex > 0
  const canScrollRight = currentIndex < data.articles.length - (isMobile ? 1 : isTablet ? 2 : 3)

  return (
    <section className={styles.container}>
      <h2 className={styles.sectionTitle}>
        {data.title}
      </h2>
      <div 
        className={styles.articlesContainer}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className={styles.carouselButtons}>
          <button 
            className={`${styles.prevButton} `}
            aria-label="Previous slide"
            onClick={handlePrevClick}
            disabled={!canScrollLeft}
          >
            <ReactSVG className={styles.pictogram} src={BackArrowSvg} />
          </button>
          <button 
            className={`${styles.nextButton} `}
            aria-label="Next slide"
            onClick={handleNextClick}
            disabled={!canScrollRight}
          >
            <ReactSVG className={styles.pictogram} src={ArrowSvg} />
          </button>
        </div>
        {visibleArticles.map((article, index) => (
          <div key={article.slug || index} className={styles.articleCardContainer}>
            <ArticleCard 
              image={article.thumbnailImage?.url ?? article.image?.url ?? ""}
              title={article.thumbnailTitle?.trim() ? article.thumbnailTitle : article.articleTitle}
              infos={truncateText(article.description, 210)} 
              bgColor={article.thumbnailBackgroundColor?.hex ?? "#162B44"}
              textColor={article.thumbnailTextColor?.hex ?? "#FFFFFF"}
              iconColor={article.thumbnailIconColor?.hex ?? "#162B44"}
              circleColor={article.thumbnailCircleColor?.hex ?? "#FFFF74"}
              link={locale === defaultLocale
                ? `/${projectsNewsInsightsPageSlug}/${article.slug}`
                : `/${locale}/${projectsNewsInsightsPageSlug}/${article.slug}`}
                style={{height: "100%"}}
            />
          </div>
        ))}
      </div>
    </section>
  )
}

export const fragment = graphql`
  fragment FurtherInformationsFields on DatoCmsThematicFurtherInformation {
    __typename
    id
    title
    articles {
      articleTitle
      preposition
      authorPosition
      description
      date
      slug
      category {
        label
      }
      image {
        alt
        format
        title
        url
      }
      author
      thumbnailImage {
        alt
        format
        url
      }
      thumbnailTitle
      thumbnailBackgroundColor {
        hex
      }
      thumbnailTextColor {
        hex
      }
      thumbnailIconColor {
        hex
      }
      thumbnailCircleColor {
        hex
      }
    }
  }
`

export default FurtherInformations
